import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { StatusBadgeComponent } from '../status-badge.component';
import { UploadDropzoneComponent } from '../upload/upload-dropzone.component';
import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';
import {
  documentBadgeIcon,
  documentBadgeLabel,
  documentBadgeTone,
  type DocumentCardBadge,
} from 'app/shared/utils/document-card-status';
import type {
  DocumentCardFile,
  DocumentCardMenu,
  DocumentCardPrimaryAction,
  DocumentCardUploadMode,
  DocumentCardVariant,
} from './document-card.types';

const PRIMARY_ACTION_LABELS: Record<Exclude<DocumentCardPrimaryAction, null>, string> = {
  upload: 'Uploaden',
  replace: 'Vervangen',
  retry: 'Opnieuw uploaden',
  complete: 'Gegevens aanvullen',
};

/** Reuses the card's existing selectors so e2e specs keep working across both variants. */
const PRIMARY_ACTION_DATA_CY: Record<Exclude<DocumentCardPrimaryAction, null>, string> = {
  upload: 'document-upload-button',
  replace: 'document-replace-button',
  retry: 'document-replace-button',
  complete: 'document-complete-button',
};

/**
 * The one document card (#20856). Extracted verbatim from the `documents-cards` entity template,
 * which is the leading design, so that the required-documents checklist and readonly-onboarding
 * render the same card instead of three hand-maintained copies of it.
 *
 * Presentational and DI-free: it derives nothing but its own labels (badge wording, expiry line,
 * short dates) from the shared `document-card-status` helpers, and emits an intent per action.
 * Data loading, permissions and the write path stay with the hosting surface.
 */
@Component({
  selector: 'app-document-card',
  imports: [
    MatIconModule,
    MatMenuModule,
    MatProgressSpinner,
    StatusBadgeComponent,
    UploadDropzoneComponent,
    TippyTooltipDirective,
  ],
  template: `
    <!--
      Rijvariant (Figma 3802:165013, component "Item list" Type=Detailed/Horizontal). Bewust
      dunner dan de kaart: geen ...-menu, geen bestandsblok, geen notice/afkeurreden — het
      onboarding-ontwerp leidt al die acties door de upload-modal. De rij-ritmiek zelf
      (padding-top, hairline, onderlinge afstand) hoort bij de LIJST die deze rijen rendert,
      niet hier: alleen de lijst weet welke rij de laatste is en dus geen scheidingslijn krijgt.
    -->
    @if (variant() === 'row') {
      <div class="flex items-start gap-3" [attr.data-cy]="dataCy()">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <!-- text-base = 0.875rem = 14px in deze repo's hertekende schaal (tailwind.config.js:72);
                 Figma zet titel en hint allebei op Inter Medium 14px / lh 1.4. -->
            <span class="text-base font-medium leading-[1.4] text-default">
              {{ title() }}
              @if (required()) {
                <span aria-hidden="true"> *</span><span class="sr-only"> (verplicht)</span>
              }
            </span>
            @if (showBadge()) {
              <app-status-badge
                [label]="badgeLabel()"
                [variant]="badgeVariant()"
                [icon]="badgeIcon()"></app-status-badge>
            }
            <!--
              Geen "Optioneel"-markering in deze variant: het ontwerp draagt verplicht/optioneel
              uitsluitend via de asterisk achter de titel (rij 4 en 5 van 3802:165013 hebben er
              geen). De sr-only "(verplicht)" hierboven houdt dat ook voor schermlezers leesbaar.
            -->
          </div>
          @if (description()) {
            <p class="m-0 text-base font-medium leading-[1.4] text-[var(--dd-card-muted-fg)]">{{ description() }}</p>
          }
        </div>
        @if (primaryAction(); as action) {
          <!--
            Figma "Type=Secondary, Size=Small": 32px hoog, 12px zijpadding, radius 8, label 14px
            Medium. h-8/px-3/rounded-lg/text-base komen daar exact op uit. De kleur MOET van
            --dd-btn-secondary-* komen en niet van --dd-badge-neutral-* (wat de kaartvariant
            hierboven gebruikt): die twee zijn in light identiek (#EEEFF1) maar lopen in dark uit
            elkaar — #545559 (Fill/Secondary/Default) tegenover #2C2C2E (background/subdued).
          -->
          <button
            type="button"
            class="inline-flex h-8 flex-none cursor-pointer items-center rounded-lg border-0 bg-[var(--dd-btn-secondary-fill)]
                   px-3 text-base font-medium leading-none text-[var(--dd-btn-secondary-label)]
                   transition-opacity hover:opacity-75 disabled:cursor-default disabled:opacity-60"
            [disabled]="disabled()"
            [attr.data-cy]="primaryActionDataCy()"
            (click)="onPrimaryAction()">
            {{ primaryActionLabel() }}
          </button>
        }
      </div>
    } @else {
      <div
        class="group flex h-full flex-col gap-5 rounded-lg border border-[var(--dd-card-border)] bg-card p-5
               transition-[border-color,box-shadow] duration-200
               hover:border-[var(--dd-card-border-strong)]
               hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.10)]"
        [attr.data-cy]="dataCy()">
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-wrap items-center gap-1.5">
            @if (showBadge()) {
              <app-status-badge [label]="badgeLabel()" [variant]="badgeVariant()" [icon]="badgeIcon()"></app-status-badge>
            }
            @if (optional()) {
              <span class="text-[12px] leading-[1.4] text-accent">Optioneel</span>
            }
          </div>
          <!-- Een kaart zonder menu-items zou een leeg paneel openen; dan geen trigger. -->
          @if (hasMenu()) {
            <button
              type="button"
              class="-mr-2 -mt-2 inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg
                     border-0 bg-transparent text-secondary transition-colors
                     hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
              [matMenuTriggerFor]="cardMenu"
              data-cy="document-actions-menu"
              aria-label="Meer acties">
              <mat-icon class="icon-size-4" svgIcon="heroicons_outline:ellipsis-horizontal"></mat-icon>
            </button>
          }
        </div>

        <h4 class="m-0 line-clamp-2 break-words text-[0.9375rem] font-medium leading-snug text-default">
          {{ title() }}
          @if (required()) {
            <span aria-hidden="true"> *</span><span class="sr-only"> (verplicht)</span>
          }
        </h4>

        @if (description()) {
          <p class="m-0 text-[0.8125rem] leading-relaxed text-[var(--dd-card-muted-fg)]">{{ description() }}</p>
        }

        @if (reviewComment(); as comment) {
          <!--
            Ingeklapt op twee regels: een lange afkeurreden maakte zijn kaart anders fors hoger dan
            de buren in hetzelfde grid. De volledige tekst zit in de tooltip (hover/focus) én achter
            een klik — dat laatste omdat een tooltip op touch niet opengaat en de reden juist het
            meest actiegerichte op een afgekeurde kaart is.
          -->
          <button
            type="button"
            class="m-0 flex w-full cursor-pointer items-start gap-2 rounded border-0
                   bg-[var(--dd-badge-negative-bg)] px-3 py-2.5 text-left text-[0.8125rem]
                   leading-snug text-[var(--dd-badge-negative-fg)]"
            [tippyTooltip]="comment"
            [tippyTooltipDisabled]="commentExpanded()"
            [attr.aria-expanded]="commentExpanded()"
            data-cy="document-card-review-comment"
            (click)="commentExpanded.set(!commentExpanded())">
            <mat-icon
              class="icon-size-4 mt-px flex-none"
              svgIcon="heroicons_outline:chat-bubble-left-ellipsis"></mat-icon>
            <span class="min-w-0 flex-1" [class.line-clamp-2]="!commentExpanded()">{{ comment }}</span>
          </button>
        }

        @if (notice()) {
          <p
            class="m-0 flex items-start gap-2 rounded bg-[var(--dd-badge-warning-bg)] px-3 py-2.5
                   text-[0.8125rem] leading-snug text-[var(--dd-badge-warning-fg)]"
            data-cy="document-card-notice">
            <mat-icon class="icon-size-4 mt-px flex-none" svgIcon="heroicons_solid:exclamation-triangle"></mat-icon>
            <span>{{ notice() }}</span>
          </p>
        }

        @if (file(); as f) {
          <div class="flex items-start gap-4 rounded-md border border-[var(--dd-card-border)] p-2.5">
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <p class="m-0 truncate text-[0.875rem] font-medium leading-tight text-default" data-cy="document-card-file">
                {{ f.name || 'Bestand' }}
              </p>
              <p
                class="m-0 flex flex-wrap items-center gap-1.5 text-[0.75rem] leading-normal text-[var(--dd-card-muted-fg)]">
                <span>Geüpload {{ uploadedAtLabel() }}</span>
                @if (expiryLabel(); as expiry) {
                  <span class="inline-block h-1 w-1 rounded-full bg-current opacity-40" aria-hidden="true"></span>
                  <span>{{ expiry }}</span>
                }
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border-0
                     bg-[var(--dd-badge-neutral-bg)] text-default transition-opacity hover:opacity-75"
              (click)="preview.emit()"
              data-cy="document-preview-inline-button"
              aria-label="Bekijk geüpload bestand">
              <mat-icon class="icon-size-4" svgIcon="heroicons_outline:eye"></mat-icon>
            </button>
          </div>
        }

        @if (busy()) {
          <div class="dd-file-row flex items-center gap-2 rounded-lg px-4 py-3 text-[0.8125rem] text-accent">
            <mat-spinner diameter="16"></mat-spinner>
            <span>Aan het uploaden…</span>
          </div>
        } @else if (uploadMode() === 'dropzone' && !file()) {
          <app-upload-dropzone
            [hint]="dropzoneHint()"
            [accept]="accept()"
            [disabled]="disabled()"
            [dropzoneLabel]="title() + ' uploaden'"
            (filesSelected)="filesSelected.emit($event)" />
        } @else if (primaryAction(); as action) {
          <div class="mt-auto flex items-center justify-end gap-3">
            <button
              type="button"
              class="inline-flex h-8 cursor-pointer items-center rounded-lg border-0 px-3 text-[0.875rem]
                     font-medium leading-none transition-opacity"
              [class]="action === 'upload' ? primaryButtonClasses : secondaryButtonClasses"
              [disabled]="disabled()"
              [attr.data-cy]="primaryActionDataCy()"
              (click)="onPrimaryAction()">
              {{ primaryActionLabel() }}
            </button>
          </div>
        }

        <!--
          Extension point for surface-specific actions the shared contract has no opinion on
          (e.g. the onboarding-review card's Goedkeuren/Afwijzen interface-action buttons). The
          card stays presentational — "the surface decides how an intent is handled" — so this
          projects host-supplied content rather than growing a new input/output pair per surface.
          An ng-content with no matching projected content renders nothing, so consumers that
          never project into this slot (documents-cards, the checklist) are pixel-unchanged.
        -->
        <ng-content select="[documentCardActions]" />
      </div>

      <mat-menu #cardMenu="matMenu">
        @if (menu().preview) {
          <button mat-menu-item type="button" data-cy="document-preview-button" (click)="preview.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:eye"></mat-icon>
            <span>Bekijk bestand</span>
          </button>
        }
        @if (menu().download) {
          <button mat-menu-item type="button" data-cy="document-download-button" (click)="download.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down-tray"></mat-icon>
            <span>Downloaden</span>
          </button>
        }
        @if (menu().history) {
          <button mat-menu-item type="button" data-cy="document-history-button" (click)="history.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:clock"></mat-icon>
            <span>Versiehistorie</span>
          </button>
        }
        @if (menu().archive) {
          <button mat-menu-item type="button" data-cy="document-archive-button" (click)="archive.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:archive-box"></mat-icon>
            <span>Archiveren</span>
          </button>
        }
        @if (menu().replace) {
          <button mat-menu-item type="button" data-cy="document-menu-replace-button" (click)="upload.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-path"></mat-icon>
            <span>Vervangen</span>
          </button>
        }
        @if (menu().complete) {
          <button mat-menu-item type="button" data-cy="document-complete-button" (click)="complete.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:pencil-square"></mat-icon>
            <span>Gegevens aanvullen</span>
          </button>
        }
        @if (menu().example) {
          <button mat-menu-item type="button" data-cy="document-template-button" (click)="example.emit()">
            <mat-icon class="icon-size-4" svgIcon="heroicons_outline:arrow-down-tray"></mat-icon>
            <span>Voorbeeld downloaden</span>
          </button>
        }
      </mat-menu>
    }
  `,
  host: { '[attr.data-variant]': 'variant()' },
  styles: `
    :host {
      display: block;
    }

    /* Alleen de kaartvariant is een gridtegel. De rij staat in een verticale lijst in een
       formulierkolom; daar zou height:100% hem oprekken en min-width hem laten overlopen. */
    :host([data-variant='card']) {
      height: 100%;
      /* Ondergrens, geen vaste breedte: de kaart volgt zijn container (gridkolom of
         formulierkolom), maar onder ~17rem wordt de bestandsregel met zijn oog-knop onleesbaar
         krap. Zonder deze grens kromp een kaart met korte teksten in een shrink-to-fit-context
         tot ~220px terwijl de buren 400px breed waren. */
      min-width: 17rem;
    }

    /* De globale outline-reset (fuse tailwind.scss) maakt custom buttons focus-onzichtbaar;
       repo-idioom is per-component compenseren met :focus-visible (WCAG 2.4.7). */
    button:focus-visible {
      outline: 2px solid var(--dd-focus-ring) !important;
      outline-offset: 2px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentCardComponent {
  readonly title = input.required<string>();
  readonly description = input<string | null>(null);
  readonly showBadge = input(true);
  /** Combined review + validity verdict; label, colour and icon are derived from it. */
  readonly badge = input.required<DocumentCardBadge>();
  /** Rejection feedback from the reviewer, rendered as a callout above the file block. */
  readonly reviewComment = input<string | null>(null);
  /**
   * Waarschuwing die actie van de gebruiker vraagt zonder een afkeuring te zijn — bijvoorbeeld
   * verplichte gegevens die na het uploaden nog ontbreken.
   */
  readonly notice = input<string | null>(null);
  readonly file = input<DocumentCardFile | null>(null);
  readonly menu = input<DocumentCardMenu>({});
  readonly primaryAction = input<DocumentCardPrimaryAction>(null);
  readonly uploadMode = input<DocumentCardUploadMode>('button');
  /**
   * Layout. `card` stays the default, so every consumer that does not ask for anything else
   * (documents-cards, the Bedrijven checklist, readonly-onboarding) renders exactly as before.
   */
  readonly variant = input<DocumentCardVariant>('card');
  /** Marks the document as mandatory — renders a `*` after the title. */
  readonly required = input(false);
  /** Renders an "Optioneel" marker beside the badge. */
  readonly optional = input(false);
  readonly disabled = input(false);
  /** Bestandsopslag loopt: sleepzone en knop wijken voor een voortgangsregel. */
  readonly busy = input(false);
  /** `accept` for the dropzone's file picker; ignored in button mode. */
  readonly accept = input('');
  readonly dropzoneHint = input('');
  readonly dataCy = input<string | null>(null);

  readonly preview = output<void>();
  readonly download = output<void>();
  readonly history = output<void>();
  readonly archive = output<void>();
  /** Every capture intent — upload, replace and retry all open the same runner. */
  readonly upload = output<void>();
  /** Download of the template's blank example file. */
  readonly example = output<void>();
  /** Reopen the create-form to fill in missing metadata, keeping the current file. */
  readonly complete = output<void>();
  /** Only in `dropzone` upload mode. */
  readonly filesSelected = output<FileList>();

  // Volledige literal class-strings: Tailwind's scanner leest bronbestanden als tekst en emit
  // alleen een utility die hij letterlijk ziet staan — een samengestelde string bereikt de
  // gecompileerde stylesheet nooit.
  protected readonly primaryButtonClasses = 'bg-black text-white dark:bg-white dark:text-black hover:opacity-90';
  protected readonly secondaryButtonClasses = 'bg-[var(--dd-badge-neutral-bg)] text-default hover:opacity-75';

  /** Ingeklapte afkeurreden; klik wisselt naar de volledige tekst (touch-pad naast de tooltip). */
  protected readonly commentExpanded = signal(false);

  protected readonly badgeLabel = computed(() => documentBadgeLabel(this.badge()));
  protected readonly badgeVariant = computed(() => documentBadgeTone(this.badge()));
  protected readonly badgeIcon = computed(() => documentBadgeIcon(this.badge()));

  protected readonly hasMenu = computed(() => Object.values(this.menu()).some(Boolean));

  protected readonly primaryActionLabel = computed(() => {
    const action = this.primaryAction();
    return action ? PRIMARY_ACTION_LABELS[action] : '';
  });

  protected readonly primaryActionDataCy = computed(() => {
    const action = this.primaryAction();
    return action ? PRIMARY_ACTION_DATA_CY[action] : null;
  });

  /** `complete` is its own intent; every other primary action opens the same create-form runner. */
  protected onPrimaryAction(): void {
    if (this.primaryAction() === 'complete') this.complete.emit();
    else this.upload.emit();
  }

  protected readonly uploadedAtLabel = computed(() => formatShortDate(this.file()?.uploadedAt));

  /**
   * Second half of the meta line. Suppressed for expiring/expired documents: their verdict
   * already sits in the badge, and repeating it beside "Geüpload …" reads as two conflicting
   * statuses on one card — which also means this label never needs a warning/negative colour.
   */
  protected readonly expiryLabel = computed<string | null>(() => {
    const badge = this.badge();
    if (!badge.expiresAt) return null;
    if (badge.badge === 'expired' || badge.badge === 'expiring') return null;
    return `Geldig tot ${formatShortDate(badge.expiresAt)}`;
  });
}

/** dd-MM-yy, zoals de Figma-kaart ("Uploaded 25-01-26"). */
function formatShortDate(raw: Date | string | null | undefined): string {
  if (!raw) return '—';
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
}
